import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { GroupEdwSummaryReport } from 'src/app/core/models/reports/reports.model';
import { IdoReportsService } from 'src/app/core/services/reports/ido/ido-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  CSV_FILE_COLUMN_HEADING,
  FILE_NAME,
  GroupEdwFormGroup,
  PDF_REPORT,
} from './group-edw.constants';

@Component({
  selector: 'app-group-edw',
  templateUrl: './group-edw.component.html',
  styleUrls: ['./group-edw.component.scss'],
  standalone: false,
})
export class GroupEdwComponent implements OnInit {
  destroy = inject(DestroyRef);
  readonly input: GroupEdwFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<GroupEdwComponent> = inject(
    MatDialogRef<GroupEdwComponent>,
  );
  private idoReportsService: IdoReportsService = inject(IdoReportsService);
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  groupEdwForm!: FormGroup;
  groupEdwSummaryReport!: GroupEdwSummaryReport;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.groupEdwForm = this.fb.group({
      date: [this.currDate],
      reportFormat: [Options.PDF],
    });
  }

  onExit() {
    this.commonUtils.openExitModal(this.groupEdwForm.dirty);
  }

  radioChange(newValue: Options) {
    this.groupEdwForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof LocationEdwComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const locationCode = user?.location;
          const groupcode = user?.locationGroup;
          const formValue = this.groupEdwForm.value;
          const date = this.commonUtils.getFormattedDate(formValue.date);
          const request = {
            groupcode,
            date,
          };
          return this.idoReportsService
            .getGroupEdwSummaryReport(request, locationCode)
            .pipe(
              take(1),
              map((data) => {
                const formatType = formValue.reportFormat;

                const groupEDWSummary = data?.response?.groupEDWSummary || [];
                if (groupEDWSummary.length) {
                  if (formatType === Options.CSV) {
                    const customHeader = [
                      [
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        'EDW Location Summary',
                        '',
                        '',
                        '',
                        '',
                        '',
                      ],
                      [
                        '',
                        'Group',
                        `${data?.response.group}`,
                        '',
                        '',
                        'Group Name',
                        `${data?.response.groupName}`,
                        '',
                        '',
                        '',
                        'Date',
                        `${data?.response.date}`,
                      ],
                    ];
                    const formatedDate = formatDate(
                      new Date(),
                      'MM-dd-yyyy',
                      'en-US',
                    );

                    const customFooter = [
                      [
                        'TOTAL',
                        '',
                        `${data?.response.totalTotalStops}`,
                        `${data?.response.totalTotalPkgs}`,
                        `${data?.response.totalNumOfTotalStopswithEDW}`,
                        `${data?.response.totalNumOfTotalStopsEDWMet}`,
                        `${data?.response.totalPercentOfTotalStopsEDWMet}`,
                        `${data?.response.totalNumOfCommittedStopswithEDW}`,
                        `${data?.response.totalNumOfCommittedStopsEDWMet}`,
                        `${data?.response.totalPercentOfCommittedStopsEDWMet}`,
                      ],
                      ['Printed:', `${formatedDate}`],
                    ];
                    this.reportUtils.exportCSVReport(
                      groupEDWSummary,
                      FILE_NAME,
                      customHeader,
                      customFooter,
                      CSV_FILE_COLUMN_HEADING,
                    );
                  }
                  if (formatType === Options.PDF) {
                    this.groupEdwSummaryReport = data;
                    setTimeout(() => {
                      const content =
                        this.pdfContainer.element.nativeElement.innerHTML;
                      this.reportUtils.exportPdfReport(
                        content,
                        PDF_REPORT.NAME,
                        FILE_NAME,
                        'l',
                      );
                    }, 0);
                  }
                } else {
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.NO_DATA_AVAILABLE,
                  );
                }
                this.dialogRef.close();
              }),
            );
        }),
      )
      .subscribe();
  }
}
